<script lang="ts">
	import { onMount } from "svelte";
	import { slide } from "svelte/transition";
	import { yyyy, goto } from "./store.svelte";
	import { toast } from "./lib/toast.svelte.ts";
	import Fa from "svelte-fa";
	import {
		faChevronDown,
		faChevronRight,
		faCircleNotch,
		faCheck,
		faTimes,
		faMagnifyingGlass,
		faExclamationCircle,
		faCheckCircle
	} from "@fortawesome/free-solid-svg-icons";

	// Store for annotation data
	let annotations = $state([]);
	let livewires = $state([]);
	let expandedAnnotations = $state({});
	let loading = $state(true);
	let error = $state(null);
	let ready = $state(false);

	// Filter states
	let searchTerm = $state("");
	let selectedLivewires = $state(new Set());
	let resolvedFilter = $state("all"); // "all", "resolved", "unresolved"
	let sortOrder = $state("newest"); // "newest", "oldest"
	let livewireDropdownOpen = $state(false);

	// Photo modal state
	let showPhotoModal = $state(false);
	let modalPhotoUrl = $state("");
	let modalPhotoCaption = $state("");
	let modalPhotos = $state([]);
	let currentPhotoIndex = $state(0);

	// Filtered annotations based on search and filters
	const filteredAnnotations = $derived(
		annotations
			.filter(annotation => {
				// Filter by livewire selection
				if (selectedLivewires.size > 0 && !selectedLivewires.has(annotation.wireident)) {
					return false;
				}

				// Filter by resolved status
				if (resolvedFilter === "resolved" && !annotation.is_resolved) return false;
				if (resolvedFilter === "unresolved" && annotation.is_resolved) return false;

				// Filter by search term
				if (searchTerm) {
					const search = searchTerm.toLowerCase();
					return (
						annotation.topic.toLowerCase().includes(search) ||
						annotation.details.toLowerCase().includes(search) ||
						annotation.this_is.toLowerCase().includes(search)
					);
				}

				return true;
			})
			.sort((a, b) => {
				const dateA = new Date(a.created_at);
				const dateB = new Date(b.created_at);
				return sortOrder === "newest" ? dateB - dateA : dateA - dateB;
			})
	);

	// Toggle expansion for a specific annotation
	const toggleAnnotation = (annotationId) => {
		const isCurrentlyExpanded = expandedAnnotations[annotationId];
		expandedAnnotations = {};
		if (!isCurrentlyExpanded) {
			expandedAnnotations[annotationId] = true;
		}
		expandedAnnotations = { ...expandedAnnotations };
	};

	// Fetch user's livewires
	const fetchLivewires = async () => {
		try {
			const url = yyyy.uri.api;
			const headers = {
				"Content-Type": "application/json",
				"Authorization": "Bearer " + yyyy.sesh.logtoken.token,
			};

			const response = await fetch(`${url}/livewires?select=wireident,livewire`, {
				method: 'GET',
				headers: headers
			});

			if (!response.ok) throw new Error(`Error fetching livewires: ${response.statusText}`);

			const data = await response.json();
			livewires = data;

			// Initialize with all livewires selected
			selectedLivewires = new Set(data.map(lw => lw.wireident));
		} catch (err) {
			console.error("Failed to fetch livewires:", err);
			toast.error("Failed to load livewires");
		}
	};

	// Fetch annotations with photos
	const fetchAnnotations = async () => {
		loading = true;
		error = null;

		try {
			if (!yyyy.sesh.logtoken) {
				goto("/");
				return;
			}

			const url = yyyy.uri.api;
			const headers = {
				"Content-Type": "application/json",
				"Authorization": "Bearer " + yyyy.sesh.logtoken.token,
			};

			const response = await fetch(`${url}/annotated?select=*,annotation_photos(*),livewires(livewire,wireident)&order=created_at.desc`, {
				method: 'GET',
				headers: headers
			});

			if (!response.ok) throw new Error(`Error fetching annotations: ${response.statusText}`);

			const data = await response.json();
			annotations = data;

		} catch (err) {
			console.error("Failed to fetch annotations:", err);
			error = err.message;
			toast.error("Failed to load annotations");
		} finally {
			loading = false;
		}
	};

	// Toggle resolved status
	const toggleResolved = async (annotation) => {
		try {
			const url = yyyy.uri.api;
			const headers = {
				"Content-Type": "application/json",
				"Authorization": "Bearer " + yyyy.sesh.logtoken.token,
				"Prefer": "return=representation"
			};

			const newResolvedStatus = !annotation.is_resolved;
			const body = JSON.stringify({
				is_resolved: newResolvedStatus,
				resolved_by: newResolvedStatus ? yyyy.sesh.tokeninfo.email : null,
				resolved_at: newResolvedStatus ? new Date().toISOString() : null
			});

			const response = await fetch(`${url}/annotated?annotated_id=eq.${annotation.annotated_id}`, {
				method: 'PATCH',
				headers: headers,
				body: body
			});

			if (!response.ok) throw new Error(`Error updating annotation: ${response.statusText}`);

			// Update local state
			annotations = annotations.map(a =>
				a.annotated_id === annotation.annotated_id
					? { ...a, is_resolved: newResolvedStatus, resolved_by: newResolvedStatus ? yyyy.sesh.tokeninfo.email : null, resolved_at: newResolvedStatus ? new Date().toISOString() : null }
					: a
			);

			toast.success(newResolvedStatus ? "Marked as resolved" : "Marked as unresolved");
		} catch (err) {
			console.error("Failed to update annotation:", err);
			toast.error("Failed to update annotation");
		}
	};

	// Toggle livewire filter
	const toggleLivewireFilter = (wireident) => {
		if (selectedLivewires.has(wireident)) {
			selectedLivewires.delete(wireident);
		} else {
			selectedLivewires.add(wireident);
		}
		selectedLivewires = new Set(selectedLivewires);
	};

	// Select/deselect all livewires
	const toggleAllLivewires = () => {
		if (selectedLivewires.size === livewires.length) {
			selectedLivewires = new Set();
		} else {
			selectedLivewires = new Set(livewires.map(lw => lw.wireident));
		}
	};

	// Open photo modal
	const openPhotoModal = (photos, index) => {
		modalPhotos = photos;
		currentPhotoIndex = index;
		modalPhotoUrl = photos[index].url;
		modalPhotoCaption = photos[index].caption || "";
		showPhotoModal = true;
	};

	// Navigate photos in modal
	const nextPhoto = () => {
		currentPhotoIndex = (currentPhotoIndex + 1) % modalPhotos.length;
		modalPhotoUrl = modalPhotos[currentPhotoIndex].url;
		modalPhotoCaption = modalPhotos[currentPhotoIndex].caption || "";
	};

	const prevPhoto = () => {
		currentPhotoIndex = (currentPhotoIndex - 1 + modalPhotos.length) % modalPhotos.length;
		modalPhotoUrl = modalPhotos[currentPhotoIndex].url;
		modalPhotoCaption = modalPhotos[currentPhotoIndex].caption || "";
	};

	// Get livewire name from embedded data
	const getLivewireName = (annotation) => {
		return annotation.livewires?.livewire || annotation.wireident;
	};

	// Format date
	const formatDate = (dateString) => {
		if (!dateString) return "";
		const date = new Date(dateString);
		return date.toLocaleString();
	};

	onMount(() => {
		if (!yyyy.sesh.logtoken) {
			goto("/");
			return;
		}

		setTimeout(() => {
			ready = true;
		}, 400);

		fetchLivewires();
		fetchAnnotations();
	});
</script>

<div class="triage-container">
	<div class="triage-content">
		<h1 class="title thin lvwr-title" class:dim={!ready} class:bri={ready}>
			Annotation Triage
		</h1>
		<h2 class="subtitle lvwr-subtitle" style="margin-bottom: 2rem;" class:dim={!ready} class:bri={ready}>
			Review and manage field annotations
		</h2>

		<div class="content lvwr-content" class:dim={!ready} class:bri={ready}>
			<!-- Filters Section -->
			<div class="filters-container">
				<!-- Search and Filter Controls -->
				<div class="search-filter-row">
					<div class="search-box">
						<input
							type="search"
							bind:value={searchTerm}
							placeholder="Search annotations..."
							class="search-input"
						/>
						<div class="search-icon">
							<Fa icon={faMagnifyingGlass} />
						</div>
					</div>

					<div class="filter-controls">
						<!-- Livewire Filter Dropdown -->
						{#if livewires.length > 0}
							<div class="filter-group livewire-dropdown-container">
								<label>Livewires:</label>
								<div class="livewire-dropdown">
									<button
										class="filter-select livewire-dropdown-btn"
										onclick={() => livewireDropdownOpen = !livewireDropdownOpen}
									>
										{selectedLivewires.size === livewires.length ? 'All Livewires' :
										 selectedLivewires.size === 0 ? 'None Selected' :
										 `${selectedLivewires.size} Selected`}
										<span class="dropdown-arrow">{livewireDropdownOpen ? '▲' : '▼'}</span>
									</button>
									{#if livewireDropdownOpen}
										<div class="livewire-dropdown-menu">
											<div class="dropdown-header">
												<button class="toggle-all-link" onclick={toggleAllLivewires}>
													{selectedLivewires.size === livewires.length ? 'Deselect All' : 'Select All'}
												</button>
											</div>
											{#each livewires as lw}
												<label class="dropdown-item">
													<input
														type="checkbox"
														checked={selectedLivewires.has(lw.wireident)}
														onchange={() => toggleLivewireFilter(lw.wireident)}
													/>
													<span>{lw.livewire}</span>
												</label>
											{/each}
										</div>
									{/if}
								</div>
							</div>
						{/if}

						<!-- Resolved Status Filter -->
						<div class="filter-group">
							<label>Status:</label>
							<select bind:value={resolvedFilter} class="filter-select">
								<option value="all">All</option>
								<option value="unresolved">Unresolved</option>
								<option value="resolved">Resolved</option>
							</select>
						</div>

						<!-- Sort Order -->
						<div class="filter-group">
							<label>Sort:</label>
							<select bind:value={sortOrder} class="filter-select">
								<option value="newest">Newest First</option>
								<option value="oldest">Oldest First</option>
							</select>
						</div>
					</div>
				</div>
			</div>

			<!-- Annotations List -->
			<div class="annotations-content">
				{#if loading}
					<div class="loading-state">
						<Fa icon={faCircleNotch} spin size="2x" />
						<p>Loading annotations...</p>
					</div>
				{:else if error}
					<div class="error-state">
						<p>Error: {error}</p>
						<button onclick={fetchAnnotations} class="retry-button">Retry</button>
					</div>
				{:else if annotations.length === 0}
					<div class="empty-state">No annotations available</div>
				{:else if filteredAnnotations.length === 0}
					<div class="empty-state">No annotations match your filters</div>
				{:else}
					<div class="annotations-list">
						{#each filteredAnnotations as annotation (annotation.annotated_id)}
							<div class="annotation-item">
								<div class="annotation-header" onclick={() => toggleAnnotation(annotation.annotated_id)}>
									<span class="expand-icon">
										<Fa icon={expandedAnnotations[annotation.annotated_id] ? faChevronDown : faChevronRight} />
									</span>
									<span class="status-icon">
										<Fa
											icon={annotation.is_resolved ? faCheckCircle : faExclamationCircle}
											color={annotation.is_resolved ? "#48c774" : "#ff9800"}
										/>
									</span>
									<div class="annotation-summary">
										<span class="annotation-topic">{annotation.topic}</span>
										<span class="annotation-meta">
											<span class="livewire-name">{getLivewireName(annotation)}</span>
											<span class="wireident-display">({annotation.wireident})</span>
											<span class="separator">•</span>
											<span class="created-date">{formatDate(annotation.created_at)}</span>
										</span>
									</div>
									{#if annotation.annotation_photos && annotation.annotation_photos.length > 0}
										<span class="photo-count-badge">{annotation.annotation_photos.length} photo{annotation.annotation_photos.length !== 1 ? 's' : ''}</span>
									{/if}
								</div>

								{#if expandedAnnotations[annotation.annotated_id]}
									<div class="annotation-details" transition:slide={{ duration: 200 }}>
										<!-- Details Section -->
										<div class="detail-section">
											<h4>Details</h4>
											<p class="details-text">{annotation.details}</p>
										</div>

										<!-- Device Info -->
										<div class="detail-section">
											<h4>Device Information</h4>
											<div class="info-grid">
												<div class="info-item">
													<span class="info-label">Type:</span>
													<span class="info-value">{annotation.this_is}</span>
												</div>
												<div class="info-item">
													<span class="info-label">Device ID:</span>
													<span class="info-value">{annotation.lw_id}</span>
												</div>
											</div>
										</div>

										<!-- Created/Resolved Info -->
										<div class="detail-section">
											<h4>Status Information</h4>
											<div class="info-grid">
												<div class="info-item">
													<span class="info-label">Created by:</span>
													<span class="info-value">{annotation.created_by}</span>
												</div>
												<div class="info-item">
													<span class="info-label">Created at:</span>
													<span class="info-value">{formatDate(annotation.created_at)}</span>
												</div>
												{#if annotation.is_resolved}
													<div class="info-item">
														<span class="info-label">Resolved by:</span>
														<span class="info-value">{annotation.resolved_by || 'N/A'}</span>
													</div>
													<div class="info-item">
														<span class="info-label">Resolved at:</span>
														<span class="info-value">{formatDate(annotation.resolved_at)}</span>
													</div>
												{/if}
											</div>
										</div>

										<!-- Photos Section -->
										{#if annotation.annotation_photos && annotation.annotation_photos.length > 0}
											<div class="detail-section">
												<h4>Photos ({annotation.annotation_photos.length})</h4>
												<div class="photo-gallery">
													{#each annotation.annotation_photos as photo, index}
														<div
															class="photo-thumbnail"
															onclick={() => openPhotoModal(annotation.annotation_photos, index)}
														>
															<img src={photo.url} alt={photo.caption || 'Annotation photo'} />
															{#if photo.caption}
																<div class="photo-caption-preview">{photo.caption}</div>
															{/if}
														</div>
													{/each}
												</div>
											</div>
										{/if}

										<!-- Actions -->
										<div class="annotation-actions">
											<button
												class="action-btn resolve-btn"
												class:is-resolved={annotation.is_resolved}
												onclick={() => toggleResolved(annotation)}
											>
												<Fa icon={annotation.is_resolved ? faTimes : faCheck} />
												{annotation.is_resolved ? 'Mark Unresolved' : 'Mark Resolved'}
											</button>
										</div>
									</div>
								{/if}
							</div>
						{/each}
					</div>
				{/if}
			</div>
		</div>

		<footer class="card-footer" class:dim={!ready} class:bri={ready}>
			<div class="card-footer-item sign-in-up-footer">
				<button
					onclick={() => goto("/livewires")}
					class="button lvwr-toggle"
				>
					Back to Livewires
				</button>
			</div>
		</footer>
	</div>
</div>

<!-- Photo Modal -->
{#if showPhotoModal}
	<div class="modal-backdrop" onclick={() => showPhotoModal = false}>
		<div class="modal-content" onclick={(e) => e.stopPropagation()}>
			<button class="modal-close" onclick={() => showPhotoModal = false}>
				<Fa icon={faTimes} size="2x" />
			</button>

			<img src={modalPhotoUrl} alt="Full size photo" class="modal-photo" />

			{#if modalPhotoCaption}
				<div class="modal-caption">{modalPhotoCaption}</div>
			{/if}

			{#if modalPhotos.length > 1}
				<button class="modal-nav modal-nav-prev" onclick={prevPhoto}>‹</button>
				<button class="modal-nav modal-nav-next" onclick={nextPhoto}>›</button>
				<div class="modal-counter">{currentPhotoIndex + 1} / {modalPhotos.length}</div>
			{/if}
		</div>
	</div>
{/if}

<style>
	.triage-container {
		position: fixed;
		top: 0;
		bottom: 0;
		left: 0;
		right: 0;
		margin: auto;
		width: 80%;
		max-width: 1200px;
		height: 80vh;
		padding: 0 1rem;
		z-index: 5;
	}

	.triage-content {
		background-color: rgba(255, 255, 255, 0.95);
		backdrop-filter: blur(2px);
		border: 1px solid #dbdbdb;
		border-radius: 8px;
		padding: 2rem;
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
		height: 100%;
		display: flex;
		flex-direction: column;
		overflow: hidden;
	}

	.dim {
		opacity: 0%;
		transition: opacity 2.5s ease;
	}

	.bri {
		opacity: 100%;
		transition: opacity 2.5s ease;
	}

	.content.lvwr-content {
		flex: 1;
		display: flex;
		flex-direction: column;
		min-height: 0;
	}

	/* Filters */
	.filters-container {
		margin-bottom: 1.5rem;
	}

	.search-filter-row {
		display: flex;
		gap: 1rem;
		align-items: center;
		margin-bottom: 1rem;
	}

	.search-box {
		position: relative;
		flex: 1;
	}

	.search-input {
		width: 100%;
		padding: 0.75rem 2.5rem 0.75rem 1rem;
		border: 1px solid #dbdbdb;
		border-radius: 4px;
		font-size: 0.9rem;
		transition: all 0.2s;
		background-color: rgba(255, 255, 255, 0.95);
		caret-color: #ff3e00;
	}

	.search-input:focus {
		outline: none;
		border-color: #ff3e00;
		box-shadow: 0 0 0 2px rgba(255, 62, 0, 0.1);
	}

	.search-icon {
		position: absolute;
		right: 0.75rem;
		top: 50%;
		transform: translateY(-50%);
		color: #999;
		pointer-events: none;
	}

	.filter-controls {
		display: flex;
		gap: 1rem;
		flex-wrap: nowrap;
	}

	.filter-group {
		display: flex;
		align-items: center;
		gap: 0.5rem;
	}

	.filter-group label {
		font-size: 0.85rem;
		font-weight: 600;
		color: #363636;
	}

	.filter-select {
		padding: 0.75rem;
		border: 1px solid #dbdbdb;
		border-radius: 4px;
		font-size: 0.9rem;
		background-color: white;
		cursor: pointer;
		height: 100%;
	}

	.filter-select:focus {
		outline: none;
		border-color: #ff3e00;
	}

	/* Livewire Dropdown */
	.livewire-dropdown-container {
		position: relative;
	}

	.livewire-dropdown {
		position: relative;
	}

	.livewire-dropdown-btn {
		display: flex;
		align-items: center;
		justify-content: space-between;
		min-width: 180px;
		text-align: left;
	}

	.dropdown-arrow {
		margin-left: 0.5rem;
		font-size: 0.7rem;
		color: #999;
	}

	.livewire-dropdown-menu {
		position: absolute;
		top: calc(100% + 4px);
		left: 0;
		background: white;
		border: 1px solid #dbdbdb;
		border-radius: 4px;
		box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
		z-index: 100;
		min-width: 200px;
		max-height: 300px;
		overflow-y: auto;
	}

	.dropdown-header {
		padding: 0.5rem 0.75rem;
		border-bottom: 1px solid #eee;
		display: flex;
		justify-content: flex-end;
	}

	.toggle-all-link {
		background: none;
		border: none;
		color: #ff3e00;
		font-size: 0.75rem;
		cursor: pointer;
		text-decoration: underline;
		padding: 0;
	}

	.toggle-all-link:hover {
		color: #e63600;
	}

	.dropdown-item {
		display: flex;
		align-items: center;
		gap: 0.5rem;
		padding: 0.5rem 0.75rem;
		cursor: pointer;
		transition: background-color 0.2s;
		font-size: 0.85rem;
	}

	.dropdown-item:hover {
		background-color: rgba(255, 62, 0, 0.05);
	}

	.dropdown-item input[type="checkbox"] {
		cursor: pointer;
	}

	.dropdown-item span {
		flex: 1;
	}

	/* Annotations List */
	.annotations-content {
		flex: 1;
		overflow-y: auto;
		min-height: 0;
	}

	.loading-state, .empty-state, .error-state {
		text-align: center;
		padding: 2rem 1rem;
		color: #666;
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 12px;
	}

	.error-state {
		color: #ff3860;
	}

	.retry-button {
		padding: 0.5rem 1rem;
		border: 1px solid #3273dc;
		background-color: #3273dc;
		color: white;
		border-radius: 4px;
		cursor: pointer;
		transition: all 0.2s;
	}

	.retry-button:hover {
		background-color: #2366d1;
	}

	.annotations-list {
		display: flex;
		flex-direction: column;
		gap: 8px;
	}

	.annotation-item {
		border: 1px solid #dbdbdb;
		border-radius: 4px;
		overflow: hidden;
		background-color: rgba(255, 255, 255, 0.9);
	}

	.annotation-header {
		padding: 0.75rem;
		background-color: rgba(255, 255, 255, 0.95);
		display: flex;
		align-items: center;
		cursor: pointer;
		transition: all 0.2s;
		gap: 0.5rem;
	}

	.annotation-header:hover {
		background-color: rgba(255, 62, 0, 0.05);
	}

	.expand-icon {
		width: 14px;
		color: #ff3e00;
	}

	.status-icon {
		width: 20px;
	}

	.annotation-summary {
		flex: 1;
		display: flex;
		flex-direction: row;
		align-items: center;
		justify-content: space-between;
		gap: 1rem;
	}

	.annotation-topic {
		font-weight: 600;
		font-size: 0.9rem;
		color: #363636;
		flex-shrink: 0;
	}

	.annotation-meta {
		font-size: 0.75rem;
		color: #666;
		display: flex;
		align-items: center;
		gap: 0.5rem;
		flex-shrink: 0;
	}

	.separator {
		color: #ccc;
	}

	.livewire-name {
		color: #ff3e00;
		font-weight: 500;
	}

	.wireident-display {
		color: #999;
		font-size: 0.7rem;
		margin-left: 0.25rem;
	}

	.photo-count-badge {
		color: #3273dc;
		font-size: 0.7rem;
		margin-left: 0.5rem;
		white-space: nowrap;
	}

	/* Annotation Details */
	.annotation-details {
		padding: 1rem;
		background-color: rgba(255, 255, 255, 0.98);
		border-top: 1px solid #eee;
	}

	.detail-section {
		margin-bottom: 1rem;
	}

	.detail-section:last-child {
		margin-bottom: 0;
	}

	.detail-section h4 {
		font-size: 0.85rem;
		font-weight: 600;
		color: #363636;
		margin-bottom: 0.5rem;
	}

	.details-text {
		font-size: 0.85rem;
		color: #4a4a4a;
		line-height: 1.5;
		white-space: pre-wrap;
	}

	.info-grid {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
		gap: 0.5rem;
	}

	.info-item {
		display: flex;
		gap: 0.5rem;
		font-size: 0.8rem;
	}

	.info-label {
		font-weight: 600;
		color: #666;
	}

	.info-value {
		color: #363636;
	}

	/* Photo Gallery */
	.photo-gallery {
		display: grid;
		grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
		gap: 0.5rem;
	}

	.photo-thumbnail {
		position: relative;
		aspect-ratio: 1;
		border-radius: 4px;
		overflow: hidden;
		cursor: pointer;
		transition: transform 0.2s;
	}

	.photo-thumbnail:hover {
		transform: scale(1.05);
	}

	.photo-thumbnail img {
		width: 100%;
		height: 100%;
		object-fit: cover;
	}

	.photo-caption-preview {
		position: absolute;
		bottom: 0;
		left: 0;
		right: 0;
		background: rgba(0, 0, 0, 0.7);
		color: white;
		font-size: 0.65rem;
		padding: 0.25rem;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	/* Actions */
	.annotation-actions {
		display: flex;
		gap: 0.5rem;
		margin-top: 1rem;
		padding-top: 1rem;
		border-top: 1px solid #eee;
	}

	.action-btn {
		padding: 0.5rem 1rem;
		border-radius: 4px;
		border: 1px solid;
		font-size: 0.85rem;
		cursor: pointer;
		transition: all 0.2s;
		display: flex;
		align-items: center;
		gap: 0.5rem;
	}

	.resolve-btn {
		background-color: #48c774;
		border-color: #48c774;
		color: white;
	}

	.resolve-btn:hover {
		background-color: #3ec46d;
	}

	.resolve-btn.is-resolved {
		background-color: #ff9800;
		border-color: #ff9800;
	}

	.resolve-btn.is-resolved:hover {
		background-color: #f59000;
	}

	/* Photo Modal */
	.modal-backdrop {
		position: fixed;
		top: 0;
		left: 0;
		width: 100vw;
		height: 100vh;
		background-color: rgba(0, 0, 0, 0.85);
		display: flex;
		align-items: center;
		justify-content: center;
		z-index: 2000;
		backdrop-filter: blur(4px);
	}

	.modal-content {
		position: relative;
		max-width: 90vw;
		max-height: 90vh;
		display: flex;
		flex-direction: column;
		align-items: center;
	}

	.modal-close {
		position: absolute;
		top: -40px;
		right: 0;
		background: none;
		border: none;
		color: white;
		cursor: pointer;
		opacity: 0.8;
		transition: opacity 0.2s;
	}

	.modal-close:hover {
		opacity: 1;
	}

	.modal-photo {
		max-width: 100%;
		max-height: 80vh;
		border-radius: 4px;
		box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
	}

	.modal-caption {
		margin-top: 1rem;
		color: white;
		background-color: rgba(0, 0, 0, 0.6);
		padding: 0.75rem 1rem;
		border-radius: 4px;
		max-width: 80vw;
		text-align: center;
	}

	.modal-nav {
		position: absolute;
		top: 50%;
		transform: translateY(-50%);
		background-color: rgba(255, 255, 255, 0.2);
		border: none;
		color: white;
		font-size: 3rem;
		width: 60px;
		height: 60px;
		border-radius: 50%;
		cursor: pointer;
		transition: all 0.2s;
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.modal-nav:hover {
		background-color: rgba(255, 255, 255, 0.3);
	}

	.modal-nav-prev {
		left: 20px;
	}

	.modal-nav-next {
		right: 20px;
	}

	.modal-counter {
		position: absolute;
		bottom: -40px;
		color: white;
		font-size: 0.9rem;
	}

	.sign-in-up-footer {
		display: flex;
		justify-content: flex-start;
	}
</style>
